import { useState } from "react";
import { useLocation } from "wouter";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Layout } from "@/components/layout";
import { useToast } from "@/hooks/use-toast";
import { useBusinesses } from "@/hooks/use-businesses";
import { apiRequest } from "@/lib/queryClient";
import {
  Building2, CreditCard, Plus, Loader2, AlertTriangle, Settings,
  ExternalLink, Users,
} from "lucide-react";

interface LicenseData {
  allowed: boolean;
  reason: string;
  licensesUsed: number;
  licensesTotal: number;
  licenseQuantity: number;
  usedLicenses: number;
  entities: Array<{ id: number; name: string }>;
}

interface SubData {
  planName?: string | null;
  monthlyPrice?: string | number | null;
  status?: string;
  isTrial?: boolean;
  stripeSubscriptionId?: string | null;
  currentPeriodEnd?: string | null;
}

export default function BillingPage() {
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const qc = useQueryClient();
  const { businesses } = useBusinesses();

  const [showAddLicenseConfirm, setShowAddLicenseConfirm] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);

  // ── Data ─────────────────────────────────────────────────────────
  const { data: license, isLoading: licLoading } = useQuery<LicenseData>({
    queryKey: ["/api/billing/licenses"],
    queryFn: async () => {
      const res = await fetch("/api/billing/licenses", { credentials: "include" });
      return res.json();
    },
  });
  const { data: subscription, isLoading: subLoading } = useQuery<SubData | null>({
    queryKey: ["/api/billing/subscription"],
    queryFn: async () => {
      const res = await fetch("/api/billing/subscription", { credentials: "include" });
      if (!res.ok) return null;
      return res.json();
    },
    staleTime: 60_000,
  });

  // ── Derived ──────────────────────────────────────────────────────
  const apiUsed = license?.usedLicenses ?? 0;
  const apiTotal = license?.licenseQuantity ?? 0;
  // Defense-in-depth: never display used > total even if backend drifts.
  const used = Math.max(apiUsed, businesses?.length ?? 0);
  const total = Math.max(apiTotal, used, 1);
  const available = Math.max(0, total - used);
  const pct = Math.min(100, Math.round((used / Math.max(total, 1)) * 100));
  const atLimit = used >= total;

  const planName = subscription?.planName || "ZentroBooks";
  const priceText = subscription?.monthlyPrice
    ? `$${parseFloat(String(subscription.monthlyPrice)).toFixed(0)}/mo`
    : "$25/mo";
  const planStatus = subscription?.isTrial
    ? "Trial"
    : subscription?.status === "active"
      ? "Active"
      : subscription?.status === "cancelling"
        ? "Cancelling"
        : (subscription?.status || "Inactive");
  const isCancelling = subscription?.status === "cancelling";

  // ── Mutations ────────────────────────────────────────────────────
  const addLicense = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/billing/add-license");
      return res.json();
    },
    onSuccess: (data: any) => {
      if (data.checkoutUrl) {
        toast({ title: "Redirecting to secure checkout..." });
        window.location.href = data.checkoutUrl;
        return;
      }
      if (data.pending) {
        toast({ title: "License purchase processing", description: "Your plan will update momentarily." });
        setTimeout(() => {
          qc.invalidateQueries({ queryKey: ["/api/billing/licenses"] });
          qc.invalidateQueries({ queryKey: ["/api/billing/subscription"] });
        }, 3000);
        return;
      }
      toast({ title: "License added", description: data.message || "You can now create another business." });
      qc.invalidateQueries({ queryKey: ["/api/billing/licenses"] });
    },
    onError: (err: any) => {
      toast({ title: "Failed", description: err?.message || "Could not add license", variant: "destructive" });
    },
  });

  // ── Actions ──────────────────────────────────────────────────────
  const openManagePayment = async () => {
    setOpeningPortal(true);
    try {
      const res = await apiRequest("POST", "/api/billing/portal");
      const data = await res.json();
      if (data?.url) {
        if (data.url.startsWith("http")) window.location.href = data.url;
        else navigate(data.url);
      } else {
        toast({
          title: "Payment management unavailable",
          description: data?.message || "No active billing customer found.",
          variant: "destructive",
        });
      }
    } catch (err: any) {
      toast({ title: "Error", description: err?.message || "Could not open billing portal", variant: "destructive" });
    } finally {
      setOpeningPortal(false);
    }
  };

  const cancelEntireSubscription = async () => {
    try {
      const res = await fetch("/api/billing/cancel", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok || !data.cancelled) {
        throw new Error(data?.message || "Cancel failed");
      }
      const endDate = data.periodEnd
        || (subscription?.currentPeriodEnd
          ? new Date(subscription.currentPeriodEnd).toLocaleDateString()
          : "the end of your billing period");
      toast({ title: "Subscription cancelled", description: `Subscription will end on ${endDate}.` });
      setShowCancelConfirm(false);
      qc.invalidateQueries({ queryKey: ["/api/billing/subscription"] });
    } catch (err: any) {
      toast({ title: "Cancel failed", description: err?.message || "Please try again.", variant: "destructive" });
    }
  };

  // ── Render ───────────────────────────────────────────────────────
  if (licLoading || subLoading) {
    return (
      <Layout>
        <div className="max-w-3xl mx-auto space-y-6 p-6">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-48" />
          <Skeleton className="h-64" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-3xl mx-auto space-y-6 p-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Subscription &amp; Billing</h1>
          <p className="text-muted-foreground text-sm">Plan, licenses, payment, and cancellation</p>
        </div>

        {/* At-limit warning */}
        {atLimit && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>You've reached your limit</AlertTitle>
            <AlertDescription className="flex flex-wrap items-center gap-3 mt-2">
              <span>All {total} business license{total === 1 ? "" : "s"} are in use.</span>
              <Button
                size="sm"
                variant="outline"
                className="bg-white"
                onClick={() => setShowAddLicenseConfirm(true)}
                disabled={addLicense.isPending}
              >
                {addLicense.isPending ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <Plus className="h-3.5 w-3.5 mr-2" />}
                Add License ($25/mo)
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Cancellation banner */}
        {isCancelling && (
          <Alert>
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Subscription cancelling</AlertTitle>
            <AlertDescription>
              Your subscription is set to end on{" "}
              <strong>
                {subscription?.currentPeriodEnd
                  ? new Date(subscription.currentPeriodEnd).toLocaleDateString()
                  : "the end of your billing period"}
              </strong>
              . All your businesses will lose access at that time.
            </AlertDescription>
          </Alert>
        )}

        {/* Plan + Price */}
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-5 w-5" />
                  {planName}
                </CardTitle>
                <CardDescription>{priceText} per business · cancel anytime</CardDescription>
              </div>
              <Badge variant={planStatus === "Active" ? "default" : planStatus === "Cancelling" ? "destructive" : "secondary"}>
                {planStatus}
              </Badge>
            </div>
          </CardHeader>
        </Card>

        {/* Licenses */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Business Licenses
            </CardTitle>
            <CardDescription>Each business uses one license</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-end justify-between">
              <span className="text-sm text-muted-foreground">Used</span>
              <span className="text-2xl font-bold">
                {used} <span className="text-base font-normal text-muted-foreground">/ {total}</span>
              </span>
            </div>
            <Progress value={pct} className="h-2" />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{used} assigned</span>
              <span>{available} available</span>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <Card>
          <CardHeader>
            <CardTitle>Actions</CardTitle>
            <CardDescription>Manage payment, licenses, and your subscription</CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 sm:grid-cols-2">
            <Button
              variant="outline"
              className="justify-start gap-2 h-auto py-3"
              disabled={addLicense.isPending}
              onClick={() => setShowAddLicenseConfirm(true)}
            >
              {addLicense.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              <div className="text-left">
                <div className="text-sm font-medium">Add License ($25/mo)</div>
                <div className="text-xs text-muted-foreground">+1 business slot</div>
              </div>
            </Button>

            <Button
              variant="outline"
              className="justify-start gap-2 h-auto py-3"
              disabled={openingPortal}
              onClick={openManagePayment}
            >
              {openingPortal ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
              <div className="text-left">
                <div className="text-sm font-medium">Manage Payment</div>
                <div className="text-xs text-muted-foreground">Cards, invoices, receipts</div>
              </div>
            </Button>

            <Button
              variant="outline"
              className="justify-start gap-2 h-auto py-3"
              onClick={() => navigate("/settings/licenses")}
            >
              <Users className="h-4 w-4" />
              <div className="text-left">
                <div className="text-sm font-medium">Manage Businesses</div>
                <div className="text-xs text-muted-foreground">Add or remove individual businesses</div>
              </div>
            </Button>

            <Button
              variant="outline"
              className="justify-start gap-2 h-auto py-3 text-destructive hover:text-destructive"
              disabled={isCancelling}
              onClick={() => {
                setConfirmCancel(false);
                setShowCancelConfirm(true);
              }}
            >
              <AlertTriangle className="h-4 w-4" />
              <div className="text-left">
                <div className="text-sm font-medium">
                  {isCancelling ? "Already cancelling" : "Cancel Entire Subscription"}
                </div>
                <div className="text-xs text-muted-foreground">
                  {isCancelling ? "Ends at period end" : "All businesses will lose access at period end"}
                </div>
              </div>
            </Button>
          </CardContent>
        </Card>

        {/* Add License confirmation */}
        <Dialog open={showAddLicenseConfirm} onOpenChange={setShowAddLicenseConfirm}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add another business license?</DialogTitle>
              <DialogDescription className="space-y-2">
                <span className="block">
                  This will <strong>increase your subscription by $25/month</strong> and let you create one more business under your account.
                </span>
                <span className="block text-xs">
                  • Existing businesses: <strong>{used}</strong> &nbsp;•&nbsp; New limit after purchase: <strong>{Math.max(total, used) + 1}</strong>
                </span>
                <span className="block text-xs">
                  If you already have a paid Stripe subscription, the change is prorated and applied immediately.
                  Otherwise, you'll be redirected to a secure Stripe checkout page to complete the purchase.
                </span>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAddLicenseConfirm(false)}>Cancel</Button>
              <Button
                onClick={() => {
                  setShowAddLicenseConfirm(false);
                  addLicense.mutate();
                }}
                disabled={addLicense.isPending}
              >
                {addLicense.isPending ? "Processing..." : "Continue to checkout"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Cancel-entire-subscription confirmation */}
        <Dialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>Cancel entire subscription?</DialogTitle>
              <DialogDescription>
                This cancels your Stripe subscription at the end of the current billing period. All of your
                businesses listed below will lose access at that time. Use{" "}
                <button
                  type="button"
                  className="underline text-foreground"
                  onClick={() => { setShowCancelConfirm(false); navigate("/settings/licenses"); }}
                >
                  Manage Businesses
                </button>{" "}
                if you only want to drop a single business.
              </DialogDescription>
            </DialogHeader>

            <div className="rounded-md border p-3 max-h-48 overflow-auto">
              <p className="text-xs font-medium text-muted-foreground mb-2">
                {(businesses || []).length} business{(businesses || []).length === 1 ? "" : "es"} affected:
              </p>
              <ul className="text-sm space-y-1">
                {(businesses || []).map((b: any) => (
                  <li key={b.id} className="flex items-center gap-2">
                    <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                    {b.name?.trim() || "Unnamed business"}
                  </li>
                ))}
                {(businesses || []).length === 0 && (
                  <li className="italic text-muted-foreground">No businesses</li>
                )}
              </ul>
            </div>

            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={confirmCancel}
                onChange={(e) => setConfirmCancel(e.target.checked)}
              />
              <span>
                I understand <strong>all {(businesses || []).length} business{(businesses || []).length === 1 ? "" : "es"}</strong> will lose access at the end of the billing period.
              </span>
            </label>

            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowCancelConfirm(false)}>Keep my subscription</Button>
              <Button
                variant="destructive"
                disabled={!confirmCancel}
                onClick={cancelEntireSubscription}
              >
                Cancel Entire Subscription
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
